import React, { useState } from "react";
import { Header } from "Pages/OnBoarding/components";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import MaleIcon from "../assets/images/mdi_face-male.svg";
import FemaleIcon from "../assets/images/mdi_face-female.svg";

const ProceedButton = styled.button`
  flex: 1; /* Makes it take 50% width */
  padding: 10px 0;
  border-radius: 6px;
  background: #2f313b;
  color: #fff;
  font-family: sans-serif;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
`;
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 95%;
  padding: 10px;
  width: 95%; /* Ensure it doesn't overflow */
  background-color: #f7f7f7;
  font-family: sans-serif;
  overflow-x: hidden; /* Prevent horizontal scrolling */
`;

const Label = styled.label`
  color: #2f313b;

  /* Text-16px */
  font-family: sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px; /* 150% */
`;

const CustomInput = styled.input`
  font-size: 20px;
  padding: 10px;
  margin: 5px 0px;
  border: 0;
  width: 90%;
  border-radius: 5px;
  box-shadow: 0px 0px 2px 0px gray;
`;

const Description = styled.p`
  color: #262b37;

  /* Text-14px */
  font-family: sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 18.2px; /* 130% */
  margin-top: 20px;
  margin-bottom: 30px;
`;
const Row = styled.div`
  display: flex;
  gap: 16px;
`;

const Column = styled.div`
  flex: 1;
`;
const Footer = styled.footer`
  text-align: center;
  width: 95%;
  position: fixed;
  bottom: 20px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
`;

const GenderSelection = styled.div`
  display: flex;
  gap: 12px;
`;

interface GenderBoxProps {
  selected: boolean;
}

const GenderBox = styled.div<GenderBoxProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 50%;
  height: 80px;
  padding: 17px 34px;
  border-radius: 4px;
  border: 1px solid #e4e4e4;
  background: ${(props) => (props.selected ? "#E0F7FA" : "#FFF")};
  cursor: pointer;

  img {
    width: 24px;
    height: 24px;
    margin-bottom: 8px;
  }

  span {
    font-family: sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: #2f313b;
  }
`;

const ErrorText = styled.p`
  color: red;
  font-size: 0.9em;
  margin-top: 8px;
`;

const TransparentButton = styled.button`
  background-color: transparent;
  color: #848484;
  border: 2px solid #848484;
  font-family: sans-serif;
  font-size: 14px;
  font-weight: 800;
  border-radius: 5px;
  cursor: pointer;
  flex: 1;
  padding: 10px;
  display: block;

  text-align: center;
  transition: background-color 0.3s, color 0.3s;

  &:hover {
    background-color: #20564b;
    color: white;
  }
`;
const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background-color: #e0e0e0;
  margin: 20px 0;

  &::after {
    content: "";
    display: block;
    width: 85%;
    height: 100%;
    background-color: #20564b;
  }
`;

const AddBMIForm: React.FC = () => {
  const [gender, setGender] = useState<number>(1); // Default value: Male
  const [name, setName] = useState<string>(""); // User's name
  const [age, setAge] = useState<number | "">(""); // Age as a number
  const [weight, setWeight] = useState<number | "">(""); // Weight in kg
  const [height, setHeight] = useState<number | "">(""); // Height in cm
  const [bmi, setBmi] = useState<number | null>(null); // Calculated BMI
  const [error, setError] = useState<string>(""); // Error messages
  const [smoking, setSmoking] = useState<boolean | null>(null); // Smoking status

  const navigate = useNavigate();

  const onProcessed = () => {
    navigate("/scan");
  };

  const handleProceed = () => {
    // Validation
    if (
      age === "" ||
      weight === "" ||
      height === "" ||
      name.trim() === "" ||
      smoking === null
    ) {
      setError("All fields are required.");
      return;
    }

    if (age <= 0 || weight <= 0 || height <= 0) {
      setError("Age, weight, and height must be positive values.");
      return;
    }

    // Calculate BMI
    const heightInMeters = height / 100; // Convert cm to meters
    const calculatedBmi = +(weight / (heightInMeters * heightInMeters)).toFixed(
      2
    );
    setBmi(calculatedBmi);
    setError("");
    const userBMI = {
      name,
      gender,
      age,
      weight,
      height,
      bmi: calculatedBmi,
      smoking,
    };
    localStorage.setItem("userBMI", JSON.stringify(userBMI));
    onProcessed(); // Call the parent handler if needed
  };

  const handleScan = () => {
    navigate("/scan");
  };

  // Disable the Proceed button if any field is empty
  const isProceedDisabled =
    name.trim() === "" ||
    age === "" ||
    weight === "" ||
    height === "" ||
    smoking === null;

  return (
    <Container>
      <ProgressBar />
      <Header /> {/* Added Header component */}
      <Description style={{ backgroundColor: "#F4F3EF" }}>
        We need your height and weight to calculate your BMI, which helps us
        assess your overall health and determine the best insurance plan
        tailored to your needs
      </Description>
      <Label>Full Name</Label>
      <CustomInput
        type="text"
        placeholder="Enter your name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      {/* Age & Weight (Same Row) */}
      <Row style={{ width: "95%" }}>
        <Column>
          <Label>Age</Label>
          <CustomInput
            type="number"
            placeholder="Enter age"
            value={age === "" ? "" : age}
            onChange={(e) => setAge(Number(e.target.value))}
          />
        </Column>
        <Column>
          <Label>Weight (kg)</Label>
          <CustomInput
            type="number"
            placeholder="Enter weight"
            value={weight === "" ? "" : weight}
            onChange={(e) => setWeight(Number(e.target.value))}
          />
        </Column>
      </Row>
      {/* Height (Full Row) */}
      <Label>Height (cm)</Label>
      <CustomInput
        type="number"
        placeholder="Enter height in cm"
        value={height === "" ? "" : height}
        onChange={(e) => setHeight(Number(e.target.value))}
      />
      {/* Gender Label */}
      <Label>Gender</Label>
      {/* Gender Selection */}
      <GenderSelection>
        <GenderBox selected={gender === 1} onClick={() => setGender(1)}>
          <img src={MaleIcon} alt="Male" />
          <span>Male</span>
        </GenderBox>
        <GenderBox selected={gender === 2} onClick={() => setGender(2)}>
          <img src={FemaleIcon} alt="Female" />
          <span>Female</span>
        </GenderBox>
      </GenderSelection>
      {/* Smoking Label */}
      <Label>Do you smoke?</Label>
      <GenderSelection>
        <GenderBox selected={smoking === true} onClick={() => setSmoking(true)}>
          <span>Yes</span>
        </GenderBox>
        <GenderBox
          selected={smoking === false}
          onClick={() => setSmoking(false)}
        >
          <span>No</span>
        </GenderBox>
      </GenderSelection>
      {error && <p style={{ color: "red", fontSize: "0.9em" }}>{error}</p>}
      {bmi !== null && (
        <p style={{ color: "green", fontSize: "1em" }}>Your BMI is: {bmi}</p>
      )}
      <Footer>
        <ButtonRow>
          <TransparentButton onClick={handleScan}>Skip</TransparentButton>
          <ProceedButton onClick={handleProceed} disabled={isProceedDisabled}>
            Proceed
          </ProceedButton>
        </ButtonRow>
      </Footer>
    </Container>
  );
};

export default AddBMIForm;
